import * as React from 'react';

import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DayPicker } from 'react-day-picker';
import { bg, enUS, type Locale } from 'react-day-picker/locale';

import { buttonVariants } from 'src/registry/ui/Button';
import { storage } from 'src/registry/utils/storage';
import { cn } from 'src/utils/shadcn';

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {};

const calendarLocal: Record<string, Locale> = {
    'en-US': enUS,
    'bg-BG': bg,
};

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
    const locale = storage.getLanguage();
    const [month, setMonth] = React.useState(props.defaultMonth);

    return (
        <DayPicker
            onNextClick={setMonth}
            onPrevClick={setMonth}
            month={month}
            locale={calendarLocal[locale]}
            showOutsideDays={showOutsideDays}
            className={cn('p-3', className)}
            classNames={{
                months: 'relative flex flex-col sm:flex-row gap-4 sm:space-y-0',
                month: 'space-y-4',
                month_caption: 'flex justify-center pt-1.5 relative items-center',
                caption_label: 'text-sm font-medium',
                nav: 'space-x-1 z-20 flex items-center absolute top-0 w-full',
                button_previous: cn(buttonVariants({ variant: 'outline-solid' }), 'size-8 p-0 top-0 absolute left-1'),
                button_next: cn(buttonVariants({ variant: 'outline-solid' }), 'size-8 p-0 top-0 absolute right-1'),
                month_grid: 'w-full border-collapse space-y-1',
                weekdays: 'flex',
                weekday: 'text-gray-500 rounded-md w-9 font-normal text-[0.8rem] dark:text-gray-400',
                week: 'flex w-full mt-2 rounded-md overflow-hidden',
                day: 'h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-gray-100/50 [&:has([aria-selected])]:bg-gray-100 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20 dark:[&:has([aria-selected].day-outside)]:bg-gray-800/50 dark:[&:has([aria-selected])]:bg-gray-800',
                day_button: cn(
                    buttonVariants({ variant: 'ghost' }),
                    'h-9 w-9 p-0 font-normal aria-selected:opacity-100',
                ),
                range_end: 'bg-primary-700 text-gray-50 rounded-md hover:bg-primary-700 hover:text-gray-900',
                range_start: 'bg-primary-600 text-gray-50 rounded-md hover:bg-primary-600 hover:text-gray-900',
                today:
                    "bg-gray-100 rounded-md text-gray-900 dark:bg-gray-800 dark:text-gray-50 [&[aria-selected='true']]:bg-primary-600 [&[aria-selected='true']]:text-white   [&[aria-selected='true']:hover]:bg-primary-700\n" +
                    "  [&[aria-selected='true']:hover]:text-gray-900",
                outside:
                    'day-outside text-gray-500 opacity-50 aria-selected:bg-gray-100/50 aria-selected:text-gray-500 aria-selected:opacity-30 dark:text-gray-400 dark:aria-selected:bg-gray-800/50 dark:aria-selected:text-gray-400',
                disabled: 'text-gray-500 opacity-50 dark:text-gray-400',
                range_middle:
                    'aria-selected:bg-gray-100 aria-selected:text-gray-900 dark:aria-selected:bg-gray-800 dark:aria-selected:text-gray-50',
                hidden: 'invisible',
                selected: 'bg-primary-600 text-gray-50 rounded-md hover:bg-primary-600 hover:text-gray-900',
                ...classNames,
            }}
            components={{
                Chevron: ({ orientation, size: _, ...props }) => {
                    if (orientation === 'left') {
                        return <FontAwesomeIcon icon={faChevronLeft} {...props} className="size-3" />;
                    }
                    return <FontAwesomeIcon icon={faChevronRight} {...props} className="size-3" />;
                },
            }}
            {...props}
        />
    );
}
Calendar.displayName = 'Calendar';

export { Calendar };
